import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { createAvatar } from '@dicebear/core';
import { bottts } from '@dicebear/collection';
import { useGameStore } from '../store/gameStore';
import Button from '../components/common/Button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function ProfileCreation() {
  const navigate = useNavigate();
  const { createProfile } = useGameStore();
  const [name, setName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(0);
  const [selectedColor, setSelectedColor] = useState('#6366f1');

  const colors = [
    '#6366f1', '#ec4899', '#10b981', '#f59e0b',
    '#ef4444', '#8b5cf6', '#06b6d4', '#f97316'
  ];

  const avatarSeeds = Array.from({ length: 30 }, (_, i) => `robot-${i}`);

  const generateAvatarDataUri = (seed: string, size: number = 100) => {
    const avatar = createAvatar(bottts, {
      seed,
      size
    });
    const svgString = avatar.toString();
    // Use the SVG string directly as a data URI
    return `data:image/svg+xml,${encodeURIComponent(svgString)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim().length >= 2) {
      const avatarSvg = generateAvatarDataUri(avatarSeeds[selectedAvatar]);
      
      await createProfile(name.trim(), avatarSvg, selectedColor);
      navigate('/levels');
    }
  };

  return (
    <div className="min-h-screen p-4 relative">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/profiles')}
            className="mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-4xl font-bold text-white">Create Your Space Hero</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Card className="p-6 bg-white/10 backdrop-blur border-white/20">
                <Label htmlFor="name" className="text-white text-lg mb-3 block">
                  Hero Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  maxLength={20}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                  required
                />
                <p className="text-sm text-gray-400 mt-2">
                  {name.length}/20 characters
                </p>
              </Card>

              <Card className="p-6 bg-white/10 backdrop-blur border-white/20">
                <Label className="text-white text-lg mb-3 block">
                  Choose Your Color
                </Label>
                <div className="grid grid-cols-4 gap-3">
                  {colors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setSelectedColor(color)}
                      className={`w-full h-12 rounded-lg transition-all ${
                        selectedColor === color ? 'ring-2 ring-white scale-110' : ''
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </Card>
            </div>

            <div>
              <Card className="p-6 bg-white/10 backdrop-blur border-white/20">
                <Label className="text-white text-lg mb-3 block">
                  Select Your Robot Avatar
                </Label>
                <div className="grid grid-cols-5 gap-3 max-h-96 overflow-y-auto">
                  {avatarSeeds.map((seed, index) => {
                    const avatar = generateAvatarDataUri(seed, 60);
                    
                    return (
                      <button
                        key={seed}
                        type="button"
                        onClick={() => setSelectedAvatar(index)}
                        className={`p-2 rounded-lg transition-all ${
                          selectedAvatar === index 
                            ? 'ring-2 ring-white scale-110 bg-white/20' 
                            : 'hover:bg-white/10'
                        }`}
                      >
                        <img src={avatar} alt={`Avatar ${index + 1}`} className="w-full h-full" />
                      </button>
                    );
                  })}
                </div>
              </Card>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8"
          >
            <Card className="p-6 bg-gradient-to-r from-purple-900/50 to-pink-900/50 backdrop-blur border-white/20">
              <h3 className="text-xl font-semibold text-white mb-4">Preview</h3>
              <div className="flex items-center gap-4">
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: selectedColor }}
                >
                  <img 
                    src={generateAvatarDataUri(avatarSeeds[selectedAvatar], 60)} 
                    alt="Selected avatar" 
                  />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {name || 'Your Name'}
                  </p>
                  <p className="text-gray-400">Ready for adventure!</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <div className="mt-8 flex justify-center">
            <Button
              type="submit"
              size="lg"
              gradient
              disabled={name.trim().length < 2}
              className="px-12"
            >
              Create Profile
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}